import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Message } from 'primereact/message';
import { Tag } from 'primereact/tag';
import { serversAPI } from '../services/api';
import { Server, ServerStatus } from '../types';

const statusOptions: { label: string; value: ServerStatus }[] = [
  { label: 'Online', value: 'online' },
  { label: 'Offline', value: 'offline' },
  { label: 'Održavanje', value: 'maintenance' }
];

const statusSeverity: Record<ServerStatus, 'success' | 'danger' | 'warning'> = {
  online: 'success',
  offline: 'danger',
  maintenance: 'warning'
};

const ITServers: React.FC = () => {
  const toast = useRef<Toast>(null);

  const [servers, setServers] = useState<Server[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [globalFilter, setGlobalFilter] = useState('');

  const [editVisible, setEditVisible] = useState(false);
  const [editing, setEditing] = useState<Server | null>(null);
  const [form, setForm] = useState({
    name: '',
    hostname: '',
    ipAddress: '',
    macAddress: '',
    operatingSystem: '',
    status: 'online' as ServerStatus,
    cpu: '',
    memory: '',
    storage: '',
    location: '',
    purpose: '',
    description: ''
  });

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await serversAPI.getAll();
      setServers(data);
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.error(e);
      setError(e?.response?.data?.error || 'Greška pri učitavanju servera');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const resetForm = () => {
    setEditing(null);
    setForm({
      name: '',
      hostname: '',
      ipAddress: '',
      macAddress: '',
      operatingSystem: '',
      status: 'online',
      cpu: '',
      memory: '',
      storage: '',
      location: '',
      purpose: '',
      description: ''
    });
  };

  const openNew = () => {
    resetForm();
    setEditVisible(true);
  };

  const openEdit = (row: Server) => {
    setEditing(row);
    setForm({
      name: row.name || '',
      hostname: row.hostname || '',
      ipAddress: row.ipAddress || '',
      macAddress: row.macAddress || '',
      operatingSystem: row.operatingSystem || '',
      status: row.status || 'online',
      cpu: row.cpu || '',
      memory: row.memory || '',
      storage: row.storage || '',
      location: row.location || '',
      purpose: row.purpose || '',
      description: row.description || ''
    });
    setEditVisible(true);
  };

  const confirmDelete = (row: Server) => {
    confirmDialog({
      header: 'Potvrda brisanja',
      message: `Obrisati server "${row.name}"?`,
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        try {
          await serversAPI.delete(row._id);
          toast.current?.show({ severity: 'success', summary: 'Uspešno', detail: 'Server obrisan', life: 2500 });
          await load();
        } catch (e: any) {
          toast.current?.show({
            severity: 'error',
            summary: 'Greška',
            detail: e?.response?.data?.error || 'Greška pri brisanju servera',
            life: 5000
          });
        }
      }
    });
  };

  const canSubmit = useMemo(() => {
    return (
      form.name.trim() &&
      form.hostname.trim() &&
      form.ipAddress.trim() &&
      form.operatingSystem.trim() &&
      form.cpu.trim() &&
      form.memory.trim() &&
      form.storage.trim() &&
      form.location.trim() &&
      form.purpose.trim()
    );
  }, [form]);

  const handleSubmit = async () => {
    if (!canSubmit) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Validacija',
        detail: 'Popunite sva obavezna polja označena zvezdicom',
        life: 3000
      });
      return;
    }

    setLoading(true);
    try {
      if (editing) {
        await serversAPI.update(editing._id, {
          ...form
        });
        toast.current?.show({ severity: 'success', summary: 'Uspešno', detail: 'Server ažuriran', life: 2500 });
      } else {
        await serversAPI.create(form);
        toast.current?.show({ severity: 'success', summary: 'Uspešno', detail: 'Server kreiran', life: 2500 });
      }
      setEditVisible(false);
      await load();
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.error(e);
      toast.current?.show({
        severity: 'error',
        summary: 'Greška',
        detail: e?.response?.data?.error || 'Greška pri čuvanju servera',
        life: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const statusBody = (row: Server) => (
    <Tag value={statusOptions.find((s) => s.value === row.status)?.label || row.status} severity={statusSeverity[row.status]} />
  );

  const actionsBody = (row: Server) => (
    <div className="flex gap-2">
      <Button
        icon="pi pi-pencil"
        className="p-button-sm p-button-rounded p-button-text"
        onClick={() => openEdit(row)}
        tooltip="Uredi"
      />
      <Button
        icon="pi pi-trash"
        className="p-button-sm p-button-rounded p-button-text p-button-danger"
        onClick={() => confirmDelete(row)}
        tooltip="Obriši"
      />
    </div>
  );

  return (
    <div className="p-4">
      <Toast ref={toast} />
      <ConfirmDialog />

      <div className="flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h2 className="m-0">Upravljanje serverima</h2>
          <div className="text-600 mt-1">Pregled i administracija servera</div>
        </div>
        <div className="flex gap-2">
          <Button label="Dodaj Server" icon="pi pi-plus" onClick={openNew} />
          <Button label="Osveži" icon="pi pi-refresh" severity="secondary" onClick={load} />
        </div>
      </div>

      {error && <Message severity="error" text={error} className="mb-3" />}

      <Card>
        <div className="flex justify-content-end mb-3">
          <span className="p-input-icon-left" style={{ width: 'min(380px, 100%)' }}>
            <i className="pi pi-search" />
            <InputText
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              placeholder="Pretraži servere..."
              style={{ width: '100%' }}
            />
          </span>
        </div>

        <DataTable
          value={servers}
          loading={loading}
          paginator
          rows={10}
          emptyMessage="Nema servera"
          className="p-datatable-sm"
          globalFilter={globalFilter}
          globalFilterFields={['name', 'hostname', 'ipAddress', 'operatingSystem', 'location', 'purpose']}
        >
          <Column field="name" header="Naziv" sortable />
          <Column field="hostname" header="Hostname" sortable />
          <Column field="ipAddress" header="IP adresa" sortable />
          <Column field="operatingSystem" header="Operativni sistem" sortable />
          <Column field="location" header="Lokacija" sortable />
          <Column field="purpose" header="Svrha" sortable />
          <Column field="status" header="Status" body={statusBody} sortable style={{ width: '140px' }} />
          <Column header="Akcije" body={actionsBody} style={{ width: '140px' }} />
        </DataTable>
      </Card>

      <Dialog
        header={editing ? 'Izmeni Server' : 'Dodaj Server'}
        visible={editVisible}
        onHide={() => setEditVisible(false)}
        style={{ width: '900px' }}
        breakpoints={{ '960px': '95vw', '640px': '98vw' }}
        modal
        draggable={false}
        footer={
          <div className="flex justify-content-end gap-2">
            <Button
              label="Otkaži"
              className="p-button-secondary p-button-outlined"
              onClick={() => setEditVisible(false)}
            />
            <Button
              label={editing ? 'Sačuvaj' : 'Dodaj'}
              icon="pi pi-check"
              onClick={handleSubmit}
              loading={loading}
              disabled={!canSubmit}
            />
          </div>
        }
      >
        <div className="p-fluid formgrid grid">
          <div className="field col-12 md:col-6">
            <label>Naziv *</label>
            <InputText value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
          </div>
          <div className="field col-12 md:col-6">
            <label>Hostname *</label>
            <InputText value={form.hostname} onChange={(e) => setForm((p) => ({ ...p, hostname: e.target.value }))} />
          </div>
          <div className="field col-12 md:col-6">
            <label>IP Adresa *</label>
            <InputText value={form.ipAddress} onChange={(e) => setForm((p) => ({ ...p, ipAddress: e.target.value }))} />
          </div>
          <div className="field col-12 md:col-6">
            <label>MAC Adresa</label>
            <InputText value={form.macAddress} onChange={(e) => setForm((p) => ({ ...p, macAddress: e.target.value }))} />
          </div>
          <div className="field col-12 md:col-6">
            <label>Operativni sistem *</label>
            <InputText
              value={form.operatingSystem}
              onChange={(e) => setForm((p) => ({ ...p, operatingSystem: e.target.value }))}
            />
          </div>
          <div className="field col-12 md:col-6">
            <label>Status *</label>
            <Dropdown
              value={form.status}
              options={statusOptions}
              onChange={(e) => setForm((p) => ({ ...p, status: e.value }))}
              optionLabel="label"
              optionValue="value"
              appendTo="self"
            />
          </div>
          <div className="field col-12 md:col-4">
            <label>CPU *</label>
            <InputText value={form.cpu} onChange={(e) => setForm((p) => ({ ...p, cpu: e.target.value }))} />
          </div>
          <div className="field col-12 md:col-4">
            <label>RAM *</label>
            <InputText value={form.memory} onChange={(e) => setForm((p) => ({ ...p, memory: e.target.value }))} />
          </div>
          <div className="field col-12 md:col-4">
            <label>Storage *</label>
            <InputText value={form.storage} onChange={(e) => setForm((p) => ({ ...p, storage: e.target.value }))} />
          </div>
          <div className="field col-12 md:col-6">
            <label>Lokacija *</label>
            <InputText value={form.location} onChange={(e) => setForm((p) => ({ ...p, location: e.target.value }))} />
          </div>
          <div className="field col-12 md:col-6">
            <label>Svrha *</label>
            <InputText value={form.purpose} onChange={(e) => setForm((p) => ({ ...p, purpose: e.target.value }))} />
          </div>
          <div className="field col-12">
            <label>Opis</label>
            <InputTextarea
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              rows={4}
              autoResize
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default ITServers;






